/**
 * Office Manager Report API hooks — Phase 10A.
 *
 * GET /v1/reports/office-manager        → OfficeManagerReport (full)
 * GET /v1/reports/office-manager/action-items → ActionItemsResponse
 * GET /v1/reports/office-manager/today  → TodayScheduleResponse
 *
 * Auto-refreshes every 5 minutes in the daily variant.
 * PDF/CSV downloads trigger a browser fetch + blob URL.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '@core/api/client';

// ── Types ────────────────────────────────────────────────────────────────────

export type Severity = 'critical' | 'high' | 'medium' | 'low';

export interface SessionBreakdown {
  total: number;
  confirmed: number;
  cancelled: number;
  completed: number;
  in_progress: number;
}

export interface TodaySnapshot {
  sessions: SessionBreakdown;
  bookings_created_today: number;
  payments_today_count: number;
  payments_today_total: number;
  new_students_today: number;
  trial_inquiries_pending: number;
  sessions_awaiting_attendance: number;
}

export interface ActionItem {
  type: string;
  severity: Severity;
  count: number;
  total_amount: number | null;
  items: unknown[];
}

export interface ActionItemsSection {
  overdue_invoices: ActionItem;
  expiring_entitlements: ActionItem;
  attendance_pending: ActionItem;
  trial_unconfirmed: ActionItem;
  low_balance_students: ActionItem;
}

export interface ScheduleSession {
  session_id: string;
  starts_at: string;
  ends_at: string | null;
  subject_name: string;
  teacher_name: string | null;
  room_name: string | null;
  student_count: number;
  status: string;
}

export interface ActivityEntry {
  entry_id: string;
  user_display: string;
  action: string;
  entity_type: string;
  entity_id: string;
  summary: string;
  created_at: string;
}

export interface HeatmapCell {
  room_id: string;
  room_name: string;
  day: string;
  hour: number;
  sessions_count: number;
  utilization_pct: number;
}

export interface HeatmapData {
  cells: HeatmapCell[];
  rooms: string[];
  days: string[];
}

export interface TeacherWorkloadEntry {
  teacher_id: string;
  teacher_name: string;
  sessions_count: number;
  hours_total: number;
  utilization_pct: number | null;
}

export interface OfficeManagerReport {
  variant: 'daily' | 'weekly';
  date_or_period: string;
  today_at_a_glance: TodaySnapshot;
  action_items: ActionItemsSection;
  today_schedule: ScheduleSession[];
  recent_activity: ActivityEntry[];
  capacity_heatmap: HeatmapData | null;
  teacher_workload: TeacherWorkloadEntry[] | null;
  generated_at: string;
  cache_hit: boolean;
  errors: string[];
}

export interface ActionItemsResponse {
  tenant_id: string;
  date: string;
  action_items: ActionItemsSection;
  generated_at: string;
}

export interface TodayScheduleResponse {
  tenant_id: string;
  date: string;
  today_at_a_glance: TodaySnapshot;
  today_schedule: ScheduleSession[];
  generated_at: string;
}

// ── Query keys ───────────────────────────────────────────────────────────────

export const REPORTS_KEYS = {
  officeManager: (variant: string, date?: string) =>
    ['reports', 'office-manager', variant, date ?? 'today'] as const,
  officeManagerActionItems: (date?: string) =>
    ['reports', 'office-manager', 'action-items', date ?? 'today'] as const,
  officeManagerToday: (date?: string) =>
    ['reports', 'office-manager', 'today', date ?? 'today'] as const,
} as const;

// ── Hooks ────────────────────────────────────────────────────────────────────

export interface UseOfficeManagerReportOptions {
  variant?: 'daily' | 'weekly';
  date?: string; // YYYY-MM-DD, default = today
  nocache?: boolean;
}

/** Full Office Manager report with 5-minute auto-refresh for daily variant. */
export function useOfficeManagerReport({
  variant = 'daily',
  date,
  nocache = false,
}: UseOfficeManagerReportOptions = {}) {
  const params = new URLSearchParams({ variant });
  if (date) params.set('date', date);
  if (nocache) params.set('nocache', '1');

  return useQuery<OfficeManagerReport>({
    queryKey: REPORTS_KEYS.officeManager(variant, date),
    queryFn: () =>
      api.get<OfficeManagerReport>(`/v1/reports/office-manager?${params.toString()}`),
    refetchInterval: variant === 'daily' ? 5 * 60_000 : false,
    staleTime: 2 * 60_000,
  });
}

/** Lightweight action items only. */
export function useOfficeManagerActionItems(date?: string) {
  const params = date ? `?date=${date}` : '';
  return useQuery<ActionItemsResponse>({
    queryKey: REPORTS_KEYS.officeManagerActionItems(date),
    queryFn: () =>
      api.get<ActionItemsResponse>(`/v1/reports/office-manager/action-items${params}`),
    refetchInterval: 5 * 60_000,
    staleTime: 2 * 60_000,
  });
}

/** Lightweight today's schedule. */
export function useOfficeManagerToday(date?: string) {
  const params = date ? `?date=${date}` : '';
  return useQuery<TodayScheduleResponse>({
    queryKey: REPORTS_KEYS.officeManagerToday(date),
    queryFn: () =>
      api.get<TodayScheduleResponse>(`/v1/reports/office-manager/today${params}`),
    refetchInterval: 5 * 60_000,
    staleTime: 2 * 60_000,
  });
}

// ── Export helpers ────────────────────────────────────────────────────────────

/**
 * Download Office Manager report as PDF or CSV.
 * Triggers a browser download via a Blob URL.
 */
export async function exportOfficeManagerReport({
  variant = 'daily',
  date,
  format,
  token,
}: {
  variant?: 'daily' | 'weekly';
  date?: string;
  format: 'pdf' | 'csv';
  token: string;
}): Promise<void> {
  const params = new URLSearchParams({ variant, format });
  if (date) params.set('date', date);

  const response = await fetch(
    `/v1/reports/office-manager?${params.toString()}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );

  if (!response.ok) {
    const err = await response.json().catch(() => ({ message: 'Export failed' }));
    throw new Error(err.detail?.message ?? err.message ?? 'Export failed');
  }

  const blob = await response.blob();
  const ext = format === 'pdf' ? 'pdf' : 'csv';
  const dateLabel = date ?? new Date().toISOString().split('T')[0];
  const filename = `office_manager_report_${dateLabel}.${ext}`;

  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Formatting helpers ───────────────────────────────────────────────────────

export const SEVERITY_COLORS: Record<Severity, string> = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#eab308',
  low: '#94a3b8',
};

export const SEVERITY_ICONS: Record<Severity, string> = {
  critical: '',
  high: '',
  medium: '',
  low: '⚪',
};

export function formatTime(isoString: string): string {
  try {
    return new Date(isoString).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return isoString;
  }
}

/** Format an integer paise value as ₹ (INR). All backend money fields are integer paise. */
export function formatCurrencyINR(paise: number): string {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(paise / 100);
}

